import React, { useState, useEffect, useCallback } from "react";
import {
  ChevronDown,
  Calendar,
  Search,
  Filter,
  RefreshCw,
  Plus,
  Eye,
  Edit2,
  Trash2,
  DollarSign,
  Sliders,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";
import { TrendUp, ExportCurve, Add } from "iconsax-reactjs";
import apiService from "../../services/api";
import CreateOpportunityModal from "../../components/CreateOpportunityModal";
import OpportunityDetailsModal from "../../components/OpportunityDetailsModal";
import { useNavigate } from "react-router-dom";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import AdvancedOpportunityFilterModal from "../../components/AdvancedOpportunityFilterModal";
import { useTheme } from "../../contexts/ThemeContext";

const Opportunities = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState({
    totalOpportunities: 0,
    totalValue: 0,
    avgDealSize: 0,
    conversionRate: 0,
    wonCount: 0,
    lostCount: 0,
    openCount: 0,
  });
  const [pipeline, setPipeline] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load pagination from session storage or use defaults
  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem("opportunitiesPaginations");
    return saved
      ? JSON.parse(saved)
      : {
          page: 1,
          limit: 100,
          total: 0,
          totalPages: 0,
        };
  });

  // Load advanced filters from session storage
  const [advancedFilters, setAdvancedFilters] = useState(() => {
    const saved = sessionStorage.getItem("opportunitiesAdvancedFilters");
    return saved ? JSON.parse(saved) : null;
  });

  const [activePresetId, setActivePresetId] = useState(() => {
    const saved = sessionStorage.getItem("opportunitiesActivePresetId");
    return saved ? JSON.parse(saved) : null;
  });

  const [activePresetName, setActivePresetName] = useState(() => {
    const saved = sessionStorage.getItem("opportunitiesActivePresetName");
    return saved ? JSON.parse(saved) : null;
  });

  const [showAdvancedFilter, setShowAdvancedFilter] = useState(false);

  // Load basic filters from session storage
  const [filters, setFilters] = useState(() => {
    const saved = sessionStorage.getItem("opportunitiesFilters");
    return saved
      ? JSON.parse(saved)
      : {
          stage: "",
          salesStatus: "",
          assignedTo: "",
          search: "",
          pipeline: "",
          sortBy: "createdAt",
          sortOrder: "DESC",
        };
  });

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [users, setUsers] = useState([]);

  // Save filters to session storage whenever they change
  useEffect(() => {
    sessionStorage.setItem("opportunitiesFilters", JSON.stringify(filters));
  }, [filters]);

  // Save advanced filters to session storage
  useEffect(() => {
    if (advancedFilters) {
      sessionStorage.setItem(
        "opportunitiesAdvancedFilters",
        JSON.stringify(advancedFilters),
      );
    } else {
      sessionStorage.removeItem("opportunitiesAdvancedFilters");
    }
  }, [advancedFilters]);

  // Save pagination to session storage
  useEffect(() => {
    sessionStorage.setItem(
      "opportunitiesPaginations",
      JSON.stringify(pagination),
    );
  }, [pagination]);

  // Save preset info to session storage
  useEffect(() => {
    if (activePresetId) {
      sessionStorage.setItem(
        "opportunitiesActivePresetId",
        JSON.stringify(activePresetId),
      );
    } else {
      sessionStorage.removeItem("opportunitiesActivePresetId");
    }
  }, [activePresetId]);

  useEffect(() => {
    if (activePresetName) {
      sessionStorage.setItem(
        "opportunitiesActivePresetName",
        JSON.stringify(activePresetName),
      );
    } else {
      sessionStorage.removeItem("opportunitiesActivePresetName");
    }
  }, [activePresetName]);

  const handleRealtimeUpdate = useCallback((data) => {
    console.log("📡 Received real-time opportunity update:", data);

    if (data.type === "opportunity_updated") {
      const { action, data: opportunityData } = data;

      setOpportunities((prevOpportunities) => {
        if (action === "created") {
          return [opportunityData, ...prevOpportunities];
        } else if (
          action === "updated" ||
          action === "sold" ||
          action === "stage_changed"
        ) {
          return prevOpportunities.map((opp) =>
            opp.id === opportunityData.id
              ? { ...opp, ...opportunityData }
              : opp,
          );
        } else if (action === "deleted") {
          return prevOpportunities.filter(
            (opp) => opp.id !== opportunityData.id,
          );
        }
        return prevOpportunities;
      });

      fetchStats();
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["opportunity_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  // Reset to page 1 when search term changes
  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [filters.search]);

  useEffect(() => {
    fetchOpportunities();
    fetchStats();
    fetchUsers();
  }, [pagination.page, filters, advancedFilters]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);

      let response;

      if (advancedFilters) {
        response = await apiService.getOpportunitiesAdvanced({
          page: pagination.page,
          limit: pagination.limit,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder,
          advancedFilters: JSON.stringify(advancedFilters),
          filterPresetId: activePresetId,
        });
      } else {
        response = await apiService.getOpportunities({
          page: pagination.page,
          limit: pagination.limit,
          ...filters,
        });
      }

      setOpportunities(response.data);
      setPagination(response.pagination);
    } catch (error) {
      console.error("Error fetching opportunities:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyAdvancedFilters = async (filterConfig, presetId) => {
    setAdvancedFilters(filterConfig);
    setActivePresetId(presetId);
    setPagination({ ...pagination, page: 1 });

    if (presetId) {
      try {
        const response = await apiService.getFilterPreset(presetId);
        setActivePresetName(response.data.name);
      } catch (error) {
        console.error("Error fetching preset name:", error);
      }
    } else {
      setActivePresetName(null);
    }
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilters(null);
    setActivePresetId(null);
    setActivePresetName(null);
    setPagination({ ...pagination, page: 1 });
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getOpportunityStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchPipeline = async () => {
    try {
      const response = await apiService.getOpportunityPipeline();
      setPipeline(response.data);
    } catch (error) {
      console.error("Error fetching pipeline:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await apiService.getCompanyUsers();
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCreateSuccess = () => {
    fetchOpportunities();
    fetchStats();
    setShowCreateModal(false);
  };

  const handleUpdateOpportunity = async (opportunityId, data) => {
    try {
      await apiService.updateOpportunity(opportunityId, data);
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      console.error("Error updating opportunity:", error);
    }
  };

  const handleDeleteOpportunity = async (opportunityId) => {
    if (window.confirm("Are you sure you want to delete this opportunity?")) {
      try {
        await apiService.deleteOpportunity(opportunityId);
        fetchOpportunities();
        fetchStats();
        setSelectedOpportunity(null);
      } catch (error) {
        console.error("Error deleting opportunity:", error);
      }
    }
  };

  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatStage = (stage) => {
    return stage
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleStageChange = async (opportunityId, newStage) => {
    try {
      await apiService.updateOpportunityStage(opportunityId, newStage);
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      console.error("Error updating stage:", error);
    }
  };

  const getSalesStatusColor = (salesStatus) => {
    const colors = {
      none: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      appointment_booked: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      not_sat: "text-red-400 bg-red-400/10 border-red-400/20",
      sat_non_qual: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      sat_qual: "text-green-400 bg-green-400/10 border-green-400/20",
      sold: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
      dead: "text-gray-500 bg-gray-500/10 border-gray-500/20",
      cancelled: "text-red-500 bg-red-500/10 border-red-500/20",
    };
    return (
      colors[salesStatus] || "text-gray-400 bg-gray-400/10 border-gray-400/20"
    );
  };

  const formatSalesStatus = (salesStatus) => {
    if (!salesStatus) return "None";
    return salesStatus
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleSalesStatusChange = async (opportunityId, newSalesStatus) => {
    try {
      await apiService.updateOpportunity(opportunityId, {
        salesStatus: newSalesStatus,
      });
      fetchOpportunities();
      fetchStats();
    } catch (error) {
      console.error("Error updating sales status:", error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      open: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      won: "text-green-400 bg-green-400/10 border-green-400/20",
      lost: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return colors[status] || "text-gray-400 bg-gray-400/10 border-gray-400/20";
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "text-gray-400 bg-gray-400/10 border-gray-400/20",
      medium: "text-blue-400 bg-blue-400/10 border-blue-400/20",
      high: "text-orange-400 bg-orange-400/10 border-orange-400/20",
      urgent: "text-red-400 bg-red-400/10 border-red-400/20",
    };
    return (
      colors[priority] || "text-gray-400 bg-gray-400/10 border-gray-400/20"
    );
  };

  const getCurrencySymbol = (currency = "GBP") => {
    const symbols = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
      CHF: "CHF",
      CNY: "¥",
      INR: "₹",
    };
    return symbols[currency] || currency;
  };

  // Server-side sorting handler
  const handleSort = (field) => {
    setFilters((prev) => {
      const newSortOrder =
        prev.sortBy === field && prev.sortOrder === "ASC" ? "DESC" : "ASC";

      return {
        ...prev,
        sortBy: field,
        sortOrder: newSortOrder,
      };
    });

    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Get the icon for sort column
  const getSortIcon = (field) => {
    if (filters.sortBy !== field) {
      return (
        <ArrowUpDown
          size={14}
          className={isDarkMode ? "text-gray-500" : "text-gray-400"}
        />
      );
    }
    return filters.sortOrder === "ASC" ? (
      <ArrowUp size={14} className="text-blue-400" />
    ) : (
      <ArrowDown size={14} className="text-blue-400" />
    );
  };

  const StatCard = ({ title, value, change, icon: Icon, currency }) => (
    <div
      className={`rounded-xl p-6 border transition-colors ${isDarkMode ? "bg-[#0C0E12] border-gray-700/50 hover:border-gray-600/50" : "bg-white border-gray-200 hover:border-gray-300"}`}
    >
      <div className="flex items-center justify-between mb-4">
        <div
          className={`text-[14px] font-medium ${isDarkMode ? "text-[#94979C]" : "text-gray-500"}`}
        >
          {title}
        </div>
        {Icon && (
          <Icon
            size={20}
            className={isDarkMode ? "text-gray-400" : "text-gray-400"}
          />
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-end justify-between">
          <div
            className={`text-[28px] font-bold ${isDarkMode ? "text-[#F7F7F7]" : "text-gray-900"}`}
          >
            {value}
          </div>
          {change !== undefined && (
            <div
              className={`flex items-center text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-700"}`}
            >
              <TrendUp className="mr-1 text-green-400" size={20} />
              {change}%
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const OpportunityRow = ({ opportunity }) => {
    const assignedUser = opportunity.assignedUser;
    const initials = assignedUser?.name
      ? assignedUser.name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
          .substring(0, 2)
      : "??";

    return (
      <tr
        className={`border-b transition-colors ${isDarkMode ? "border-gray-700 hover:bg-gray-800/50" : "border-gray-200 hover:bg-gray-50"}`}
      >
        <td className="py-4 px-4">
          <button
            onClick={() =>
              window.open(`/opportunities/${opportunity.id}`, "_blank")
            }
            className={`font-medium hover:text-blue-400 transition-colors text-left ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {opportunity.name}
          </button>
          {opportunity.lead && (
            <div
              className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {opportunity.lead.companyName}
            </div>
          )}
        </td>
        <td className="py-4 px-4">
          <div
            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {formatCurrency(opportunity.amount, opportunity.currency)}
          </div>
        </td>
        <td className="py-4 px-4">
          <span
            className={`px-3 py-1 rounded-full border text-xs font-medium inline-block ${getSalesStatusColor(
              opportunity.salesStatus,
            )}`}
          >
            {opportunity.salesStatus
              ? opportunity.salesStatus
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")
              : "None"}
          </span>
        </td>
        <td className="py-4 px-4">
          {assignedUser ? (
            <div className="flex items-center gap-2">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
              >
                {assignedUser.profileImageUrl ? (
                  <img
                    src={assignedUser.profileImageUrl}
                    alt={assignedUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  initials
                )}
              </div>
              <span
                className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}
              >
                {assignedUser.name}
              </span>
            </div>
          ) : (
            <span
              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              Unassigned
            </span>
          )}
        </td>
        <td
          className={`py-4 px-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {opportunity.expectedCloseDate
            ? new Date(opportunity.expectedCloseDate).toLocaleDateString()
            : "Not set"}
        </td>
        <td className="py-4 px-4 text-right">
          <div className="flex items-center gap-2 justify-end">
            <button
              onClick={() =>
                window.open(`/opportunities/${opportunity.id}`, "_blank")
              }
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-400 hover:text-gray-700"}`}
              title="View details"
            >
              <Eye size={16} />
            </button>
            <button
              onClick={() => handleDeleteOpportunity(opportunity.id)}
              className={`transition-colors p-1 ${isDarkMode ? "text-gray-400 hover:text-red-400" : "text-gray-400 hover:text-red-500"}`}
              title="Delete"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1
              className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              Opportunities
            </h1>
            <p
              className={`text-sm sm:text-base leading-relaxed ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Track and manage your sales opportunities and deals.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            <button
              onClick={fetchOpportunities}
              className={`flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 border rounded-lg transition-colors text-sm sm:text-base font-medium min-w-0 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-gray-300 hover:bg-gray-700" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
            >
              <RefreshCw size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Refresh</span>
            </button>

            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #427BCA36,
                  0px 8px 16px 0px #2F578F1F,
                  0px 2px 4px -8px #427BCA14,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">New Opportunity</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 mb-6">
          {/* Active Advanced Filter Badge */}
          {advancedFilters && (
            <div className="bg-purple-600/10 border border-purple-600/30 rounded-lg p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Sliders size={20} className="text-purple-400" />
                <div>
                  <div className="text-purple-400 font-medium">
                    {activePresetName
                      ? `Filter: ${activePresetName}`
                      : "Advanced Filter Active"}
                  </div>
                  <div
                    className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {advancedFilters.conditions.length} condition
                    {advancedFilters.conditions.length !== 1 ? "s" : ""} applied
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowAdvancedFilter(true)}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm"
                >
                  Edit Filter
                </button>
                <button
                  onClick={handleClearAdvancedFilters}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${isDarkMode ? "bg-gray-700 text-gray-300 hover:bg-gray-600" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
                >
                  Clear Filter
                </button>
              </div>
            </div>
          )}

          {/* Search and Filter Controls */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search
                size={16}
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${isDarkMode ? "text-gray-400" : "text-gray-400"}`}
              />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={filters.search}
                onChange={(e) =>
                  setFilters({ ...filters, search: e.target.value })
                }
                className={`w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-gray-600 ${isDarkMode ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-400" : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"}`}
              />
            </div>

            {/* Advanced Filter Button */}
            <button
              onClick={() => setShowAdvancedFilter(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #427BCA 0%, #2F578F 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
          0px 36px 32px -16px #427BCA36,
          0px 8px 16px 0px #2F578F1F,
          0px 2px 4px -8px #427BCA14,
          0px 8px 24px -16px #FFFFFF3D inset,
          0px -24px 32px 0px #FFFFFF38 inset,
          0px 0px 12px 0px #FFFFFF inset
        `,
              }}
            >
              <Sliders size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Advanced Filters</span>
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          className={`rounded-lg border mb-8 ${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"}`}
        >
          <div
            className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div>
              <h2
                className={`text-xl font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                All Opportunities{" "}
                <span
                  className={`text-sm ml-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {pagination.total} opportunities
                </span>
              </h2>
              <p
                className={`text-sm mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Manage your sales pipeline and track deal progress.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[1200px]">
              <thead className={isDarkMode ? "bg-[#13161B]" : "bg-gray-50"}>
                <tr
                  className={`border-b ${isDarkMode ? "border-[#22262F]" : "border-gray-200"}`}
                >
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <button
                      onClick={() => handleSort("name")}
                      className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                    >
                      Opportunity
                      {getSortIcon("name")}
                    </button>
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <button
                      onClick={() => handleSort("amount")}
                      className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                    >
                      Amount
                      {getSortIcon("amount")}
                    </button>
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <button
                      onClick={() => handleSort("salesStatus")}
                      className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                    >
                      Stage
                      {getSortIcon("salesStatus")}
                    </button>
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <button
                      onClick={() => handleSort("assignedUser")}
                      className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                    >
                      Assigned To
                      {getSortIcon("assignedUser")}
                    </button>
                  </th>
                  <th
                    className={`text-left py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    <button
                      onClick={() => handleSort("expectedCloseDate")}
                      className={`flex items-center gap-2 transition-colors ${isDarkMode ? "hover:text-white" : "hover:text-gray-900"}`}
                    >
                      Close Date
                      {getSortIcon("expectedCloseDate")}
                    </button>
                  </th>
                  <th
                    className={`text-right py-4 px-4 font-medium text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      Loading opportunities...
                    </td>
                  </tr>
                ) : opportunities.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className={`py-8 text-center ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                    >
                      No opportunities found. Create your first opportunity to
                      get started.
                    </td>
                  </tr>
                ) : (
                  opportunities.map((opportunity) => (
                    <OpportunityRow
                      key={opportunity.id}
                      opportunity={opportunity}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div
            className={`flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t gap-4 ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
          >
            <div className="flex gap-2">
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page - 1 })
                }
                disabled={pagination.page === 1}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Previous
              </button>
              <button
                onClick={() =>
                  setPagination({ ...pagination, page: pagination.page + 1 })
                }
                disabled={pagination.page >= pagination.totalPages}
                className={`px-3 py-1 border rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${isDarkMode ? "bg-[#0C0E12] border-[#373A41] text-gray-300 hover:bg-gray-600" : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"}`}
              >
                Next
              </button>
            </div>
            <span
              className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Page {pagination.page} of {pagination.totalPages}
            </span>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showCreateModal && (
        <CreateOpportunityModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          users={users}
        />
      )}

      {selectedOpportunity && (
        <OpportunityDetailsModal
          opportunity={selectedOpportunity}
          onClose={() => setSelectedOpportunity(null)}
          onUpdate={handleUpdateOpportunity}
          onDelete={handleDeleteOpportunity}
          users={users}
        />
      )}

      {showAdvancedFilter && (
        <AdvancedOpportunityFilterModal
          isOpen={showAdvancedFilter}
          onClose={() => setShowAdvancedFilter(false)}
          onApplyFilters={handleApplyAdvancedFilters}
        />
      )}
    </div>
  );
};

export default Opportunities;
